import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  useCreateEnglishQuestionGroupMutation,
  useUpdateEnglishQuestionGroupMutation,
} from "../../../slices/englishQuestionGroupApiSlice";

const GROUP_TYPES = [
  {
    value: "passage",
    label: "Passage",
    description: "A group of questions based on a passage",
  },
  {
    value: "clozeTest",
    label: "Cloze Test",
    description: "Questions based on a cloze passage",
  },
  {
    value: "caselet",
    label: "Caselet",
    description: "Questions based on a caselet",
  },
  {
    value: "commonDirections",
    label: "Common Directions",
    description: "Questions sharing common instructions",
  },
  {
    value: "sentenceBased",
    label: "Sentence Based",
    description: "Questions based on common sentence content",
  },
];

const initialFormState = {
  subject: "",
  chapter: "",
  groupType: "",
  directions: "",
  sharedContent: {
    type: "text",
    text: "",
    imageUrl: "",
  },
  isActive: true,
};

const EnglishQuestionGroupForm = ({
  group = null,
  subjects = [],
  chapters = [],
  onSuccess,
  onCancel,
}) => {
  const navigate = useNavigate();

  const isEditMode = Boolean(group?._id);

  const [formData, setFormData] = useState(initialFormState);

  const [errors, setErrors] = useState({});

  const [
    createEnglishQuestionGroup,
    { isLoading: isCreating, error: createError },
  ] = useCreateEnglishQuestionGroupMutation();

  const [
    updateEnglishQuestionGroup,
    { isLoading: isUpdating, error: updateError },
  ] = useUpdateEnglishQuestionGroupMutation();

  const isLoading = isCreating || isUpdating;

  // ======================================================
  // Populate Form For Edit
  // ======================================================

  useEffect(() => {
    if (!group) {
      setFormData(initialFormState);
      return;
    }

    setFormData({
      subject: group.subject?._id || group.subject || "",

      chapter: group.chapter?._id || group.chapter || "",

      groupType: group.groupType || "",

      directions: group.directions || "",

      sharedContent: {
        type: group.sharedContent?.type || "text",

        text: group.sharedContent?.text || "",

        imageUrl: group.sharedContent?.imageUrl || "",
      },

      isActive: group.isActive !== undefined ? group.isActive : true,
    });
  }, [group]);

  // ======================================================
  // Change Handlers
  // ======================================================

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((previous) => ({
      ...previous,
      [name]: value,
    }));

    setErrors((previous) => ({
      ...previous,
      [name]: "",
    }));
  };

  const handleSharedContentChange = (event) => {
    const { name, value } = event.target;

    setFormData((previous) => ({
      ...previous,

      sharedContent: {
        ...previous.sharedContent,
        [name]: value,
      },
    }));

    setErrors((previous) => ({
      ...previous,
      sharedContent: "",
    }));
  };

  const handleActiveChange = (event) => {
    setFormData((previous) => ({
      ...previous,
      isActive: event.target.checked,
    }));
  };

  // ======================================================
  // Validation
  // ======================================================

  const validateForm = () => {
    const validationErrors = {};

    if (!formData.subject) {
      validationErrors.subject = "Please select a subject";
    }

    if (!formData.groupType) {
      validationErrors.groupType = "Please select a group type";
    }

    if (!formData.directions.trim()) {
      validationErrors.directions = "Directions are required";
    }

    if (
      formData.sharedContent.type === "text" &&
      !formData.sharedContent.text.trim()
    ) {
      validationErrors.sharedContent = "Shared content is required";
    }

    if (
      formData.sharedContent.type === "image" &&
      !formData.sharedContent.imageUrl.trim()
    ) {
      validationErrors.sharedContent = "Image URL is required";
    }

    setErrors(validationErrors);

    return Object.keys(validationErrors).length === 0;
  };

  // ======================================================
  // Submit
  // ======================================================

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    const payload = {
      subject: formData.subject,

      ...(formData.chapter
        ? {
            chapter: formData.chapter,
          }
        : {}),

      groupType: formData.groupType,

      directions: formData.directions.trim(),

      sharedContent: {
        type: formData.sharedContent.type,

        text: formData.sharedContent.text.trim(),

        imageUrl: formData.sharedContent.imageUrl.trim() || null,
      },

      isActive: formData.isActive,
    };

    try {
      let response;

      if (isEditMode) {
        response = await updateEnglishQuestionGroup({
          id: group._id,
          ...payload,
        }).unwrap();
      } else {
        response = await createEnglishQuestionGroup(payload).unwrap();
      }

      if (onSuccess) {
        onSuccess(response);
      } else {
        navigate("/english-question-groups");
      }
    } catch (error) {
      console.error("English question group save error:", error);
    }
  };

  // ======================================================
  // Server Error
  // ======================================================

  const serverError = createError || updateError;

  const serverErrorMessage =
    serverError?.data?.message || serverError?.error || null;

  // ======================================================
  // Render
  // ======================================================

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* ==================================================
          Header
      ================================================== */}

      <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="mb-1">
          <h2 className="text-xl font-semibold text-gray-900">
            {isEditMode
              ? "Edit English Question Group"
              : "Create English Question Group"}
          </h2>

          <p className="mt-1 text-sm text-gray-500">
            Create a shared content group for English questions such as
            passages, cloze tests, caselets, or common directions.
          </p>
        </div>
      </div>

      {/* ==================================================
          Server Error
      ================================================== */}

      {serverErrorMessage && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <p className="text-sm font-medium text-red-700">
            {serverErrorMessage}
          </p>
        </div>
      )}

      {/* ==================================================
          Basic Information
      ================================================== */}

      <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="mb-5">
          <h3 className="text-base font-semibold text-gray-900">
            Basic Information
          </h3>

          <p className="mt-1 text-sm text-gray-500">
            Select where this question group belongs.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          {/* Subject */}

          <div>
            <label
              htmlFor="subject"
              className="mb-2 block text-sm font-medium text-gray-700"
            >
              Subject
              <span className="ml-1 text-red-500">*</span>
            </label>

            <select
              id="subject"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              className={`w-full rounded-lg border bg-white px-3 py-2.5 text-sm outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 ${
                errors.subject ? "border-red-400" : "border-gray-300"
              }`}
            >
              <option value="">Select subject</option>

              {subjects.map((subject) => (
                <option key={subject._id} value={subject._id}>
                  {subject.name}
                </option>
              ))}
            </select>

            {errors.subject && (
              <p className="mt-1 text-xs text-red-600">{errors.subject}</p>
            )}
          </div>

          {/* Chapter */}

          <div>
            <label
              htmlFor="chapter"
              className="mb-2 block text-sm font-medium text-gray-700"
            >
              Chapter
            </label>

            <select
              id="chapter"
              name="chapter"
              value={formData.chapter}
              onChange={handleChange}
              disabled={!formData.subject}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 disabled:cursor-not-allowed disabled:bg-gray-100"
            >
              <option value="">
                {formData.subject ? "Select chapter" : "Select subject first"}
              </option>

              {chapters.map((chapter) => (
                <option key={chapter._id} value={chapter._id}>
                  {chapter.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* ==================================================
          Group Type
      ================================================== */}

      <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="mb-5">
          <h3 className="text-base font-semibold text-gray-900">Group Type</h3>

          <p className="mt-1 text-sm text-gray-500">
            Select the type of shared question group you are creating.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {GROUP_TYPES.map((type) => {
            const selected = formData.groupType === type.value;

            return (
              <button
                key={type.value}
                type="button"
                onClick={() => {
                  setFormData((previous) => ({
                    ...previous,
                    groupType: type.value,
                  }));

                  setErrors((previous) => ({
                    ...previous,
                    groupType: "",
                  }));
                }}
                className={`rounded-xl border p-4 text-left transition ${
                  selected
                    ? "border-indigo-500 bg-indigo-50 ring-2 ring-indigo-100"
                    : "border-gray-200 bg-white hover:border-indigo-300 hover:bg-gray-50"
                }`}
              >
                <div className="flex items-start justify-between">
                  <span
                    className={`text-sm font-semibold ${
                      selected ? "text-indigo-700" : "text-gray-800"
                    }`}
                  >
                    {type.label}
                  </span>

                  {selected && (
                    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-indigo-600 text-xs text-white">
                      ✓
                    </span>
                  )}
                </div>

                <p className="mt-1 text-xs leading-5 text-gray-500">
                  {type.description}
                </p>
              </button>
            );
          })}
        </div>

        {errors.groupType && (
          <p className="mt-2 text-xs text-red-600">{errors.groupType}</p>
        )}
      </div>

      {/* ==================================================
          Directions
      ================================================== */}

      <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="mb-5">
          <h3 className="text-base font-semibold text-gray-900">Directions</h3>

          <p className="mt-1 text-sm text-gray-500">
            Instructions that should be displayed before the questions in this
            group.
          </p>
        </div>

        <textarea
          name="directions"
          value={formData.directions}
          onChange={handleChange}
          rows={5}
          placeholder="Example: Read the following passage carefully and answer the questions that follow."
          className={`w-full resize-y rounded-lg border px-3 py-3 text-sm outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 ${
            errors.directions ? "border-red-400" : "border-gray-300"
          }`}
        />

        {errors.directions && (
          <p className="mt-1 text-xs text-red-600">{errors.directions}</p>
        )}
      </div>

      {/* ==================================================
          Shared Content
      ================================================== */}

      <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="mb-5">
          <h3 className="text-base font-semibold text-gray-900">
            Shared Content
          </h3>

          <p className="mt-1 text-sm text-gray-500">
            Add the passage, caselet, cloze content, or other shared material.
          </p>
        </div>

        {/* Content Type */}

        <div className="mb-5">
          <label className="mb-2 block text-sm font-medium text-gray-700">
            Content Type
          </label>

          <div className="flex flex-wrap gap-3">
            <label
              className={`flex cursor-pointer items-center gap-2 rounded-lg border px-4 py-2.5 text-sm ${
                formData.sharedContent.type === "text"
                  ? "border-indigo-500 bg-indigo-50 text-indigo-700"
                  : "border-gray-300 text-gray-700"
              }`}
            >
              <input
                type="radio"
                name="sharedContentType"
                value="text"
                checked={formData.sharedContent.type === "text"}
                onChange={() =>
                  setFormData((previous) => ({
                    ...previous,
                    sharedContent: {
                      ...previous.sharedContent,
                      type: "text",
                    },
                  }))
                }
                className="text-indigo-600 focus:ring-indigo-500"
              />
              Text
            </label>

            <label
              className={`flex cursor-pointer items-center gap-2 rounded-lg border px-4 py-2.5 text-sm ${
                formData.sharedContent.type === "image"
                  ? "border-indigo-500 bg-indigo-50 text-indigo-700"
                  : "border-gray-300 text-gray-700"
              }`}
            >
              <input
                type="radio"
                name="sharedContentType"
                value="image"
                checked={formData.sharedContent.type === "image"}
                onChange={() =>
                  setFormData((previous) => ({
                    ...previous,
                    sharedContent: {
                      ...previous.sharedContent,
                      type: "image",
                    },
                  }))
                }
                className="text-indigo-600 focus:ring-indigo-500"
              />
              Image
            </label>

            <label
              className={`flex cursor-pointer items-center gap-2 rounded-lg border px-4 py-2.5 text-sm ${
                formData.sharedContent.type === "none"
                  ? "border-indigo-500 bg-indigo-50 text-indigo-700"
                  : "border-gray-300 text-gray-700"
              }`}
            >
              <input
                type="radio"
                name="sharedContentType"
                value="none"
                checked={formData.sharedContent.type === "none"}
                onChange={() =>
                  setFormData((previous) => ({
                    ...previous,
                    sharedContent: {
                      ...previous.sharedContent,
                      type: "none",
                    },
                  }))
                }
                className="text-indigo-600 focus:ring-indigo-500"
              />
              None
            </label>
          </div>
        </div>

        {/* Text Content */}

        {formData.sharedContent.type === "text" && (
          <div>
            <label
              htmlFor="sharedContentText"
              className="mb-2 block text-sm font-medium text-gray-700"
            >
              Shared Text
              <span className="ml-1 text-red-500">*</span>
            </label>

            <textarea
              id="sharedContentText"
              name="text"
              value={formData.sharedContent.text}
              onChange={handleSharedContentChange}
              rows={10}
              placeholder="Enter the passage, caselet, cloze passage, or shared sentence content..."
              className={`w-full resize-y rounded-lg border px-3 py-3 text-sm leading-6 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 ${
                errors.sharedContent ? "border-red-400" : "border-gray-300"
              }`}
            />

            {errors.sharedContent && (
              <p className="mt-1 text-xs text-red-600">
                {errors.sharedContent}
              </p>
            )}
          </div>
        )}

        {/* Image */}

        {formData.sharedContent.type === "image" && (
          <div>
            <label
              htmlFor="imageUrl"
              className="mb-2 block text-sm font-medium text-gray-700"
            >
              Image URL
              <span className="ml-1 text-red-500">*</span>
            </label>

            <input
              id="imageUrl"
              type="url"
              name="imageUrl"
              value={formData.sharedContent.imageUrl}
              onChange={handleSharedContentChange}
              placeholder="https://example.com/image.jpg"
              className={`w-full rounded-lg border px-3 py-2.5 text-sm outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 ${
                errors.sharedContent ? "border-red-400" : "border-gray-300"
              }`}
            />

            {formData.sharedContent.imageUrl && (
              <div className="mt-4 overflow-hidden rounded-lg border border-gray-200 bg-gray-50 p-3">
                <p className="mb-2 text-xs font-medium text-gray-500">
                  Preview
                </p>

                <img
                  src={formData.sharedContent.imageUrl}
                  alt="Shared content preview"
                  className="max-h-72 max-w-full rounded object-contain"
                  onError={(event) => {
                    event.currentTarget.style.display = "none";
                  }}
                />
              </div>
            )}

            {errors.sharedContent && (
              <p className="mt-1 text-xs text-red-600">
                {errors.sharedContent}
              </p>
            )}
          </div>
        )}

        {/* None */}

        {formData.sharedContent.type === "none" && (
          <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4">
            <p className="text-sm text-yellow-800">
              No shared content will be displayed for this group.
            </p>
          </div>
        )}
      </div>

      {/* ==================================================
          Status
      ================================================== */}

      <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h3 className="text-base font-semibold text-gray-900">
              Group Status
            </h3>

            <p className="mt-1 text-sm text-gray-500">
              Inactive groups will not be available when creating or generating
              quizzes.
            </p>
          </div>

          <label className="relative inline-flex cursor-pointer items-center">
            <input
              type="checkbox"
              checked={formData.isActive}
              onChange={handleActiveChange}
              className="peer sr-only"
            />

            <div className="h-6 w-11 rounded-full bg-gray-300 transition peer-checked:bg-indigo-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-100 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:after:translate-x-full peer-checked:after:border-white" />
          </label>
        </div>

        <div className="mt-3">
          <span
            className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${
              formData.isActive
                ? "bg-green-100 text-green-700"
                : "bg-gray-100 text-gray-600"
            }`}
          >
            {formData.isActive ? "Active" : "Inactive"}
          </span>
        </div>
      </div>

      {/* ==================================================
          Actions
      ================================================== */}

      <div className="flex flex-col-reverse gap-3 border-t border-gray-200 pt-5 sm:flex-row sm:justify-end">
        <button
          type="button"
          onClick={() => {
            if (onCancel) {
              onCancel();
            } else {
              navigate("/english-question-groups");
            }
          }}
          disabled={isLoading}
          className="rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Cancel
        </button>

        <button
          type="submit"
          disabled={isLoading}
          className="inline-flex items-center justify-center rounded-lg bg-indigo-600 px-5 py-2.5 text-sm font-medium text-white transition hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {isLoading && (
            <svg
              className="mr-2 h-4 w-4 animate-spin"
              viewBox="0 0 24 24"
              fill="none"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />

              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
              />
            </svg>
          )}

          {isLoading
            ? isEditMode
              ? "Updating..."
              : "Creating..."
            : isEditMode
              ? "Update Group"
              : "Create Group"}
        </button>
      </div>
    </form>
  );
};

export default EnglishQuestionGroupForm;
